import clsx from 'clsx';
import { History } from 'history';
import { symmetricDifference } from 'ramda';
import React, {
  createRef,
  useCallback,
  useEffect,
  useReducer,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import {
  TextValidator,
  ValidatorForm
} from 'react-material-ui-form-validator';
import { connect } from 'react-redux';

import {
  Button,
  CircularProgress,
  Container,
  makeStyles,
  Toolbar
} from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';

import { FormPanel } from '@checkit/react-components';
import { CancelIcon } from '@checkit/react-components/build/components/Icons';

import useWindowUnload from '../../checkpoints/components/form/hooks/usePreventWindowUnload';
import { loaderReducer } from '../../common/delayedLoader';
import { SaveIcon } from '../../common/SaveIcon';
import AlertDialog from '../../components/alert/AlertDialog';
import MaterialUiLink from '../../components/MaterialUiLink';
import RouteLeavingGuard from '../../components/routeLeaving/routeLeaving';
import config from '../../config';
import { useAxiosDispatch } from '../../middleware/axios';
import { State } from '../../state';
import { appMessages } from '../../translations';
import { getLibrary } from '../actions';
import {
  EntitySnapshotWithType,
  EntityType,
  LibraryDto
} from '../state';

import { SelectedChecksPanel } from './SelectedChecksPanel';
import { SelectedRulesPanel } from './SelectedRulesPanel';
import { SelectedSchedulesPanel } from './SelectedSchedulesPanel';
import { messages } from './translations';

const MAX_LENGTH_NAME = 150;
const MAX_LENGTH_DESC = 200;

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    margin: theme.spacing(1)
  },
  formInput: {
    marginTop: theme.spacing(0)
  },
  iconSmall: {
    fontSize: 20
  },
  leftIcon: {
    marginRight: theme.spacing(1)
  },
  spinnerContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4, 2)
  },
  toolBar: {
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center'
    },
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3)
  }
}));

export function LibraryFormImpl({
  code,
  history,
  libraries,
  handleSubmit
}: {
  /** The code of the existing library to edit (or `null` if a new library is being created). */
  code: string | null;
  /** Used to navigate back to the libraries listing page once the library has been created/updated. */
  history: History;
  /** All fetched libraries, from global redux state. */
  libraries: State['libraries'];
  /** A callback which handles saving or updating the library with updated details. */
  handleSubmit: (data: LibraryDto) => Promise<void>;
}) {
  const { formatMessage } = useIntl();
  const dispatch = useAxiosDispatch( );
  const classes = useStyles();
  const formRef = createRef() as React.RefObject<ValidatorForm>;

  // State

  /** Whether the form has been edited, and the user should therefore be warned when trying to navigate away. */
  const [dirty, setDirty] = useState(false);
  /** Whether the form is currently being submitted, and should be disabled as a result. */
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  /** Whether the user is being prompted for confirmation to submit the form. */
  const [isConfirmingSubmit, setIsConfirmingSubmit] = useState(false);

  /** The library's name */
  const [name, setName] = useState('');
  /** The library's description */
  const [description, setDescription] = useState('');
  /** The library's published status */
  const [isPublished, setIsPublished] = useState(false);

  /** The checks selected by the user for inclusion. */
  const [selectedChecks, setSelectedChecks] = useState([ ] as Array<EntitySnapshotWithType<'check'>>);
  /** The checks which are already included in the saved version of the library. */
  const [existingChecks, setExistingChecks] = useState([ ] as Array<EntitySnapshotWithType<'check'>>);

  /** The schedules selected by the user for inclusion. */
  const [selectedSchedules, setSelectedSchedules] = useState([ ] as Array<EntitySnapshotWithType<'schedule'>>);
  /** The schedules which are already included in the saved version of the library. */
  const [existingSchedules, setExistingSchedules] = useState([ ] as Array<EntitySnapshotWithType<'schedule'>>);

  /** The rules selected by the user for inclusion. */
  const [selectedRules, setSelectedRules] = useState([ ] as Array<EntitySnapshotWithType<'rule'>>);
  /** The rules which are already included in the saved version of the library. */
  const [existingRules, setExistingRules] = useState([ ] as Array<EntitySnapshotWithType<'rule'>>);

  const [ loadingState, loadingDispatch ] = useReducer(loaderReducer, {
    displayLoader: true,
    loading: false
  });

  const isEnabledRulePanel = config.workManagement.showLibraryRulePanel;
  const isEnabledSchedulePanel = config.workManagement.showLibrarySchedulePanel;
  const areSnapshotsUnchanged = symmetricDifference(existingChecks, selectedChecks).length === 0
    && symmetricDifference(existingRules, selectedRules).length === 0
    && symmetricDifference(existingSchedules, selectedSchedules).length === 0;

  useWindowUnload(dirty);
  /** Populates the library's data once it has been fetched. */
  const handleLoaded = useCallback(function populate( ) {
    const isLoadedAlready = !loadingState.loading && !loadingState.displayLoader;
    if (isLoadedAlready) {
      // already been through this, don't set the state again
      return;
    }

    if (code == null) {
      // nothing to load
      loadingDispatch('loaded');
      return;
    }

    const library = libraries.byCode[code];
    if (library == null) {
      throw new Error('Library not loaded, but handleLoaded() called');
    }
    if (!('entitySnapshots' in library.librarySnapshot)) {
      throw new Error('Library details not loaded, but handleLoaded() called');
    }

    // populate library data
    setName(library.name);
    setDescription(library.description);
    setIsPublished(library.lastPublished != null);

    const selectedEntities: {
      // partitioning the selected entity snapshots by their type
      [ type in EntityType ]: Array<EntitySnapshotWithType<type>>;
    } = {
      check: [ ],
      rule: [ ],
      schedule: [ ]
    };
    for (const entitySnapshot of library.librarySnapshot.entitySnapshots) {
      const type = entitySnapshot.type;
      // some weird type assertions here just to make typescript okay with the fact that
      // the entity snapshot has the same type as the array it is being pushed to, which
      // typescript isn't able to infer nicely
      const snap = entitySnapshot as EntitySnapshotWithType<typeof type>;
      (selectedEntities[type] as Array<EntitySnapshotWithType<typeof type>>).push(snap);
    }

    setSelectedChecks(selectedEntities.check);
    setExistingChecks(selectedEntities.check);

    setSelectedRules(selectedEntities.rule);
    setExistingRules(selectedEntities.rule);

    setSelectedSchedules(selectedEntities.schedule);
    setExistingSchedules(selectedEntities.schedule);

    // display data to user
    loadingDispatch('loaded');
  }, [code, libraries.byCode, loadingState.displayLoader, loadingState.loading]);

  useEffect(function loadLibrary( ) {
    if (code == null) {
      // nothing to load
      handleLoaded( );
      return;
    }

    const library = libraries.byCode[code];
    const isLoaded = library != null
      // ensure that detailed library data has been fetched for this library
      && 'entitySnapshots' in library.librarySnapshot;
    const isLoading = loadingState.loading;

    if (isLoaded) {
      handleLoaded( );
    } else if (!isLoading) {
      loadingDispatch('loading');
      dispatch(getLibrary(code));
    }

  }, [code, dispatch, handleLoaded, libraries.byCode, loadingState.loading]);

  useEffect(function updateDirtyForSnapshotChange( ) {
    if (!areSnapshotsUnchanged) {
      setDirty(true);
    }
  }, [areSnapshotsUnchanged]);

  useEffect(function updateDirtyForNameAndDescription( ) {
    if (code != null) {
      const library = libraries.byCode[code];
      if (library && library.name === name && library.description === description && areSnapshotsUnchanged) {
        setIsFormSubmitting(false);
        setDirty(false);
      }
    }
  }, [areSnapshotsUnchanged, code, description, libraries.byCode, name]);

  // Methods

  /** Submits the current form, including validating it first. */
  async function submit( ) {
    const form = formRef.current;
    if (form == null) {
      throw new Error('Form submitted before being initialized');
    }

    setIsConfirmingSubmit(false);

    const isValid = await form.isFormValid(false);
    if (!isValid) { return; }

    setIsFormSubmitting(true);
    setDirty(false);

    const entitySnapshots: LibraryDto['entitySnapshots'] = [
      ...selectedChecks
      // ...selectedSchedules
      // ...selectedRules
    ];
    await handleSubmit({ name, description, entitySnapshots })
      .then(( ) => history.push('/libraries'))
      // let the user edit the library again and re-try
      .catch(( ) => setIsFormSubmitting(false));
  }

  // Event Handlers

  function onFormSubmit(event: React.FormEvent) {
    event.preventDefault( );
    // once the library has been published, any snapshots added won't be able to
    // be removed anymore (as we don't keep track of which ones were added since
    // the last publish)
    const isSavingDangerous = isPublished && !areSnapshotsUnchanged;
    if (isSavingDangerous) {
      setIsConfirmingSubmit(true);
    } else {
      submit( );
    }
  }

  const onCancelConfirmation = ( ) => setIsConfirmingSubmit(false);
  const onFormChange = (_: React.ChangeEvent<HTMLInputElement>) => setDirty(true);
  const onChangeName = (event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value);
  const onChangeDescription = (event: React.ChangeEvent<HTMLInputElement>) => setDescription(event.target.value);

  return loadingState.displayLoader ? (
    <Container className={classes.spinnerContainer}>
      <CircularProgress />
    </Container>
  ) : (
    <>
      <ValidatorForm ref={formRef} onSubmit={onFormSubmit} onChange={onFormChange}>
        <FormPanel
          heading={formatMessage(messages['library.form.heading'])}
          name="library"
          initialExpanded={true}
        >
          <TextValidator
            fullWidth={true}
            id="library-name"
            className={classes.formInput}
            label={formatMessage(messages['library.form.name'])}
            name="name"
            autoFocus={true}
            value={name}
            withRequiredValidator={true}
            validators={['required', 'trim', `maxStringLength:${MAX_LENGTH_NAME}`]}
            errorMessages={[
              formatMessage(
                appMessages['app.required'],
                { fieldName: formatMessage(messages['library.form.name']) }
              ),
              formatMessage(
                appMessages['app.nonBlank'],
                { fieldName: formatMessage(messages['library.form.name']) }
              ),
              formatMessage(
                appMessages['app.maxLength'],
                {
                  fieldName: formatMessage(messages['library.form.name']),
                  maxLength: MAX_LENGTH_NAME
                }
              )
            ]}
            onChange={onChangeName}
          />
          <TextValidator
            fullWidth={true}
            id="library-description"
            className={classes.formInput}
            label={formatMessage(messages['library.form.description'])}
            name="description"
            value={description}
            validators={[`maxStringLength:${MAX_LENGTH_DESC}`]}
            errorMessages={[
              formatMessage(
                appMessages['app.maxLength'],
                {
                  fieldName: formatMessage(messages['library.form.description']),
                  maxLength: MAX_LENGTH_DESC
                }
              )
            ]}
            onChange={onChangeDescription}
          />
        </FormPanel>

        <SelectedChecksPanel
          selected={selectedChecks}
          setSelected={setSelectedChecks}
          existing={existingChecks}
          isLibrarySaved={code != null}
          isLibraryPublished={isPublished}
        />

        {isEnabledSchedulePanel ? (
          <SelectedSchedulesPanel
            selected={selectedSchedules}
            setSelected={setSelectedSchedules}
            existing={existingSchedules}
            isLibrarySaved={code != null}
          />
        ) : null}

        {isEnabledRulePanel ? (
          <SelectedRulesPanel
            selected={selectedRules}
            setSelected={setSelectedRules}
            existing={existingRules}
            isLibrarySaved={code != null}
          />
        ) : null}

        <Toolbar className={classes.toolBar}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.button}
            component={MaterialUiLink}
            to="/libraries"
          >
            <CancelIcon className={clsx(classes.leftIcon, classes.iconSmall)} />
            {formatMessage(appMessages['app.cancel'])}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={classes.button}
            disabled={isFormSubmitting || !dirty}
          >
            <SaveIcon formSubmitting={isFormSubmitting} />
            {formatMessage(appMessages['app.save'])}
          </Button>
        </Toolbar>
      </ValidatorForm>

      <RouteLeavingGuard when={dirty} />

      <AlertDialog
        open={isConfirmingSubmit}
        title={formatMessage(messages['library.form.confirm.title'])}
        text={formatMessage(messages['library.form.confirm.description'])}
        onCancel={onCancelConfirmation}
        onAccept={submit}
      />
    </>
  );
}
export const LibraryForm = connect(
  ({ libraries }: State) => ({ libraries })
)(LibraryFormImpl);
